import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  AE_INPUT,
  AE_INPUT_COMPACT,
  AE_LABEL,
  AE_LABEL_COMPACT,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';

/** Campaign-owned respondent copy (seeded from template at create). */
export interface CampaignMessagingSetupValue {
  /** Kept in sync with opening_purpose for legacy notification_config readers. */
  invite_message: string;
  opening_purpose: string;
  opening_instructions: string;
  closing_message: string;
  /** Reminder email body (Respondent experience). */
  reminder_message: string;
}

/** Reminder body without the bold “Reminder” email prefix. Keep in sync with backend defaultAeReminderMessage. */
export function defaultCampaignReminderMessage(assessmentName = ''): string {
  const name = assessmentName.trim() || 'assessment';
  return `Complete your ${name} to access latest executive peer benchmarks and insights.`;
}


interface CampaignInviteSetupFieldsProps {
  value: CampaignMessagingSetupValue;
  onChange: (
    next: CampaignMessagingSetupValue | ((prev: CampaignMessagingSetupValue) => CampaignMessagingSetupValue)
  ) => void;
  disabled?: boolean;
  /** Denser hub layout: smaller textareas and tighter gaps. */
  compact?: boolean;
  /** Campaign / assessment name for the default reminder placeholder. */
  assessmentName?: string;
}

interface SimpleFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (text: string) => void;
  disabled?: boolean;
  minHeight?: string;
  placeholder?: string;
  hint?: string;
  compact?: boolean;
}

const SimpleField: React.FC<SimpleFieldProps> = ({
  id,
  label,
  value,
  onChange,
  disabled = false,
  minHeight = 'min-h-[4rem]',
  placeholder,
  hint,
  compact = false,
}) => {
  const labelClass = compact ? AE_LABEL_COMPACT : AE_LABEL;
  const inputClass = compact ? AE_INPUT_COMPACT : AE_INPUT;

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      <label className={`${labelClass} mb-0`} htmlFor={id}>
        {label}
      </label>
      {hint && <p className={`${AE_MUTED} text-xs m-0`}>{hint}</p>}
      <textarea
        id={id}
        className={`${inputClass} ${minHeight}`}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
};

/**
 * Respondent-facing campaign copy.
 * Opening purpose is also the invitation email body (single source of truth).
 */
export const CampaignInviteSetupFields: React.FC<CampaignInviteSetupFieldsProps> = ({
  value,
  onChange,
  disabled = false,
  compact = false,
  assessmentName = '',
}) => {
  const { t } = useLanguage();
  const shortHeight = compact ? 'min-h-[2.75rem]' : 'min-h-[4rem]';
  const reminderPlaceholder = defaultCampaignReminderMessage(assessmentName);

  return (
    <div className={compact ? 'space-y-3' : 'space-y-6'}>
      <SimpleField
        id="ae-opening-purpose"
        label={t('assessmentEngine.wizard.purposeAndEmailIntro')}
        hint={t('assessmentEngine.wizard.openingPurposeInviteHint')}
        value={value.opening_purpose}
        onChange={(opening_purpose) =>
          onChange((prev) => ({
            ...prev,
            opening_purpose,
            // Keep invite_message aligned so older readers stay consistent.
            invite_message: opening_purpose,
          }))
        }
        disabled={disabled}
        compact={compact}
        minHeight={shortHeight}
      />

      <SimpleField
        id="ae-reminder-message"
        label={t('assessmentEngine.wizard.reminderEmailText')}
        hint={t('assessmentEngine.wizard.reminderEmailTextHint')}
        value={value.reminder_message}
        onChange={(reminder_message) =>
          onChange((prev) => ({ ...prev, reminder_message }))
        }
        disabled={disabled}
        compact={compact}
        minHeight={shortHeight}
        placeholder={reminderPlaceholder}
      />

      <SimpleField
        id="ae-opening-instructions"
        label={t('assessmentEngine.builder.openingInstructions')}
        value={value.opening_instructions}
        onChange={(opening_instructions) =>
          onChange((prev) => ({ ...prev, opening_instructions }))
        }
        disabled={disabled}
        compact={compact}
        minHeight={shortHeight}
      />

      <SimpleField
        id="ae-closing-message"
        label={t('assessmentEngine.builder.closingMessage')}
        value={value.closing_message}
        onChange={(closing_message) => onChange((prev) => ({ ...prev, closing_message }))}
        disabled={disabled}
        compact={compact}
        minHeight={shortHeight}
      />
    </div>
  );
};
